// material-ui
import {
    Divider,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    TextField,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Chip,
    Typography,
    Box,
    IconButton,
    Tooltip
} from '@mui/material';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { useTheme, alpha } from '@mui/material/styles';

// third party
import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'utils/axios';

// project imports
import MainCard from 'ui-component/cards/MainCard';

// assets
import { IconSearch, IconPackage, IconCoin } from '@tabler/icons';

// =========================|| DASHBOARD ANALYTICS - LATEST CUSTOMERS TABLE CARD ||========================= //

interface LatestCustomerTableCardProps {
    title?: string;
}

interface StoreRow {
    storeId: string;
    pricesSuccess: number;
    pricesErrors: number;
    inventorySuccess: number;
    inventoryErrors: number;
    totalTransactions: number;
    totalErrors: number;
    successRate: number | null;
    status: 'healthy' | 'error' | 'inactive';
    hasPrices: boolean;
    hasInventory: boolean;
    originalIndex: number;
}

type Order = 'asc' | 'desc';

function descendingComparator(a: StoreRow, b: StoreRow, orderBy: keyof StoreRow) {
    let aVal: any = a[orderBy];
    let bVal: any = b[orderBy];

    if (orderBy === 'successRate') {
        if (aVal === null) aVal = -1;
        if (bVal === null) bVal = -1;
    }

    if (typeof aVal === 'string') {
        return bVal.localeCompare(aVal);
    }

    if (bVal < aVal) {
        return -1;
    }
    if (bVal > aVal) {
        return 1;
    }
    return 0;
}

function getComparator(order: Order, orderBy: keyof StoreRow): (a: StoreRow, b: StoreRow) => number {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

const columns: { id: keyof StoreRow | 'actions'; label: string; sortable: boolean }[] = [
    { id: 'originalIndex', label: '#', sortable: true },
    { id: 'storeId', label: 'Store ID', sortable: true },
    { id: 'status', label: 'Status', sortable: true },
    { id: 'pricesSuccess', label: 'Prices Success', sortable: true },
    { id: 'pricesErrors', label: 'Prices Error', sortable: true },
    { id: 'inventorySuccess', label: 'Inventory Success', sortable: true },
    { id: 'inventoryErrors', label: 'Inventory Error', sortable: true },
    { id: 'successRate', label: 'Success Rate', sortable: true },
    { id: 'actions', label: 'Actions', sortable: false }
];

const LatestCustomerTableCard = ({ title }: LatestCustomerTableCardProps) => {
    const theme = useTheme();
    const [data, setData] = React.useState<any>();

    // Filters, Search, Sorting, and Pagination states
    const [search, setSearch] = React.useState('');
    const [statusFilter, setStatusFilter] = React.useState<'all' | 'healthy' | 'error' | 'inactive'>('all');
    const [integrationFilter, setIntegrationFilter] = React.useState<'all' | 'both' | 'prices' | 'inventory'>('all');
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof StoreRow>('storeId');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const fetchData = async () => {
        const obj: { [x: string]: { inventory: { success: number; errors: number }; prices: { success: number; errors: number } } } = {};
        const [inventoryStats, pricesStats] = await Promise.all([
            axios.get(process.env.REACT_APP_INVENTORY_SELLERS_INSIGHTS_URL || ''),
            axios.get(process.env.REACT_APP_PRICES_SELLERS_INSIGHTS_URL || '')
        ]);

        inventoryStats?.data?.forEach((item: any) => {
            obj[item.storeId] = { ...obj[item.storeId], inventory: item };
        });

        pricesStats?.data?.forEach((item: any) => {
            obj[item.storeId] = { ...obj[item.storeId], prices: item };
        });

        setData(obj);
    };

    React.useEffect(() => {
        fetchData();
    }, []);

    // Transform raw API data into rows
    const rows: StoreRow[] = React.useMemo(() => {
        if (!data) return [];
        return Object.keys(data).map((storeId, index) => {
            const storeData = data[storeId];
            const pricesSuccess = Number(storeData.prices?.success || 0);
            const pricesErrors = Number(storeData.prices?.errors || 0);
            const inventorySuccess = Number(storeData.inventory?.success || 0);
            const inventoryErrors = Number(storeData.inventory?.errors || 0);

            const pricesTransactions = pricesSuccess + pricesErrors;
            const inventoryTransactions = inventorySuccess + inventoryErrors;

            const totalTransactions = pricesTransactions + inventoryTransactions;
            const totalErrors = pricesErrors + inventoryErrors;
            const totalSuccess = pricesSuccess + inventorySuccess;

            // A store is inactive if it has no transactions in prices or no transactions in inventory
            let status: 'healthy' | 'error' | 'inactive' = 'inactive';
            if (pricesTransactions > 0 && inventoryTransactions > 0) {
                status = totalErrors > 0 ? 'error' : 'healthy';
            }

            // Success rate is null (N/A) if the store is inactive in either integration
            const successRate = pricesTransactions > 0 && inventoryTransactions > 0 ? (totalSuccess / totalTransactions) * 100 : null;

            const hasPrices = !!storeData.prices;
            const hasInventory = !!storeData.inventory;

            return {
                storeId,
                pricesSuccess,
                pricesErrors,
                inventorySuccess,
                inventoryErrors,
                totalTransactions,
                totalErrors,
                successRate,
                status,
                hasPrices,
                hasInventory,
                originalIndex: index + 1
            };
        });
    }, [data]);

    // Local filters application
    const filteredRows = React.useMemo(
        () =>
            rows.filter((row) => {
                // Search filter
                if (search && !row.storeId.toLowerCase().includes(search.toLowerCase())) {
                    return false;
                }

                // Status filter
                if (statusFilter !== 'all' && row.status !== statusFilter) {
                    return false;
                }

                // Integration filter
                if (integrationFilter !== 'all') {
                    if (integrationFilter === 'both' && (!row.hasPrices || !row.hasInventory)) {
                        return false;
                    }
                    if (integrationFilter === 'prices' && !row.hasPrices) {
                        return false;
                    }
                    if (integrationFilter === 'inventory' && !row.hasInventory) {
                        return false;
                    }
                }

                return true;
            }),
        [rows, search, statusFilter, integrationFilter]
    );

    // Sorting application
    const sortedRows = React.useMemo(() => {
        const comparator = getComparator(order, orderBy);
        const stabilizedThis = filteredRows.map((el, index) => [el, index] as [StoreRow, number]);
        stabilizedThis.sort((a, b) => {
            const ord = comparator(a[0], b[0]);
            if (ord !== 0) return ord;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    }, [filteredRows, order, orderBy]);

    // Pagination application
    const paginatedRows = React.useMemo(() => {
        const startIndex = page * rowsPerPage;
        return sortedRows.slice(startIndex, startIndex + rowsPerPage);
    }, [sortedRows, page, rowsPerPage]);

    const handleRequestSort = (property: keyof StoreRow) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusChip = (status: 'healthy' | 'error' | 'inactive') => {
        switch (status) {
            case 'healthy':
                return (
                    <Chip
                        label="Healthy"
                        size="small"
                        sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            height: '24px',
                            bgcolor:
                                theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.success.main, 0.15)
                                    : alpha(theme.palette.success.light, 0.4),
                            color: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                        }}
                    />
                );
            case 'error':
                return (
                    <Chip
                        label="Errors"
                        size="small"
                        sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            height: '24px',
                            bgcolor:
                                theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.error.main, 0.15)
                                    : alpha(theme.palette.error.light, 0.4),
                            color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark,
                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
                        }}
                    />
                );
            case 'inactive':
            default:
                return (
                    <Chip
                        label="Inactive"
                        size="small"
                        sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            height: '24px',
                            bgcolor:
                                theme.palette.mode === 'dark' ? alpha(theme.palette.grey[500], 0.15) : alpha(theme.palette.grey[300], 0.4),
                            color: theme.palette.text.secondary,
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`
                        }}
                    />
                );
        }
    };

    const getSuccessRateDisplay = (rate: number | null) => {
        if (rate === null) {
            return (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                    N/A
                </Typography>
            );
        }

        let textColor = theme.palette.error.main;
        if (rate >= 95) {
            textColor = theme.palette.success.main;
        } else if (rate >= 80) {
            textColor = theme.palette.warning.main;
        }

        return (
            <Typography variant="body2" sx={{ fontWeight: 800, color: textColor }}>
                {rate.toFixed(1)}%
            </Typography>
        );
    };

    const renderTableBodyContent = () => {
        if (!data) {
            return [...Array(5)].map((_, index) => (
                <TableRow hover key={index}>
                    {[...Array(9)].map((__, colIndex) => (
                        <TableCell key={colIndex} sx={colIndex === 0 ? { pl: 3 } : {}}>
                            <Skeleton variant="text" />
                        </TableCell>
                    ))}
                </TableRow>
            ));
        }

        if (paginatedRows.length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <Typography variant="body1" color="textSecondary">
                            No stores found matching the filters.
                        </Typography>
                    </TableCell>
                </TableRow>
            );
        }

        const getRedirectionParams = () => {
            const now = new Date();
            const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
            return `dateRangeInit=${oneHourAgo.toISOString()}&dateRangeEnd=${now.toISOString()}`;
        };

        return paginatedRows.map((row) => {
            const queryParams = getRedirectionParams();
            const isDark = theme.palette.mode === 'dark';

            let inventoryBg = 'transparent';
            if (row.hasInventory) {
                inventoryBg = isDark ? alpha(theme.palette.secondary.main, 0.15) : alpha(theme.palette.secondary.light, 0.4);
            }

            let pricesBg = 'transparent';
            if (row.hasPrices) {
                pricesBg = isDark ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.light, 0.4);
            }

            return (
                <TableRow hover key={row.storeId}>
                    <TableCell sx={{ pl: 3 }}>{row.originalIndex}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.storeId}</TableCell>
                    <TableCell>{getStatusChip(row.status)}</TableCell>
                    <TableCell>{row.pricesSuccess.toLocaleString()}</TableCell>
                    <TableCell sx={{ color: row.pricesErrors > 0 ? theme.palette.error.main : 'inherit' }}>
                        {row.pricesErrors.toLocaleString()}
                    </TableCell>
                    <TableCell>{row.inventorySuccess.toLocaleString()}</TableCell>
                    <TableCell sx={{ color: row.inventoryErrors > 0 ? theme.palette.error.main : 'inherit' }}>
                        {row.inventoryErrors.toLocaleString()}
                    </TableCell>
                    <TableCell>{getSuccessRateDisplay(row.successRate)}</TableCell>
                    <TableCell>
                        <Tooltip
                            title={row.hasInventory ? 'View Inventory Processes (Last 1 hour)' : 'Inventory integration inactive'}
                            placement="top"
                            arrow
                        >
                            <span>
                                <IconButton
                                    component={Link}
                                    to={`/process/inventory?storeId=${row.storeId}&${queryParams}`}
                                    color="secondary"
                                    size="small"
                                    disabled={!row.hasInventory}
                                    sx={{
                                        mr: 1,
                                        bgcolor: inventoryBg,
                                        '&:hover': {
                                            bgcolor: theme.palette.secondary.main,
                                            color: '#fff'
                                        }
                                    }}
                                >
                                    <IconPackage size="1.2rem" stroke={1.5} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip
                            title={row.hasPrices ? 'View Prices Processes (Last 1 hour)' : 'Prices integration inactive'}
                            placement="top"
                            arrow
                        >
                            <span>
                                <IconButton
                                    component={Link}
                                    to={`/process/prices?storeId=${row.storeId}&${queryParams}`}
                                    color="primary"
                                    size="small"
                                    disabled={!row.hasPrices}
                                    sx={{
                                        bgcolor: pricesBg,
                                        '&:hover': {
                                            bgcolor: theme.palette.primary.main,
                                            color: '#fff'
                                        }
                                    }}
                                >
                                    <IconCoin size="1.2rem" stroke={1.5} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </TableCell>
                </TableRow>
            );
        });
    };

    const showClearButton = search !== '' || statusFilter !== 'all' || integrationFilter !== 'all';

    const handleClearFilters = () => {
        setSearch('');
        setStatusFilter('all');
        setIntegrationFilter('all');
        setPage(0);
    };

    const summary = React.useMemo(() => {
        const total = rows.length;
        const healthy = rows.filter((r) => r.status === 'healthy').length;
        const errors = rows.filter((r) => r.status === 'error').length;
        const inactive = rows.filter((r) => r.status === 'inactive').length;

        let totalSuccess = 0;
        let totalErrors = 0;
        rows.forEach((r) => {
            totalSuccess += r.pricesSuccess + r.inventorySuccess;
            totalErrors += r.pricesErrors + r.inventoryErrors;
        });
        const totalTx = totalSuccess + totalErrors;
        const successRate = totalTx > 0 ? (totalSuccess / totalTx) * 100 : null;

        return {
            total,
            healthy,
            errors,
            inactive,
            successRate,
            totalSuccess,
            totalErrors
        };
    }, [rows]);

    const rateColorSettings = React.useMemo(() => {
        const rate = summary.successRate;
        const isDark = theme.palette.mode === 'dark';
        if (rate === null) {
            return {
                border: isDark ? alpha(theme.palette.divider, 0.1) : theme.palette.grey[300],
                bg: isDark ? alpha(theme.palette.common.black, 0.2) : theme.palette.grey[100],
                text: theme.palette.text.secondary,
                labelColor: 'textSecondary'
            };
        }

        if (rate >= 95) {
            return {
                border: alpha(theme.palette.success.main, 0.2),
                bg: isDark ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.success.light, 0.2),
                text: theme.palette.success.dark,
                labelColor: 'success.dark'
            };
        }

        if (rate >= 80) {
            return {
                border: alpha(theme.palette.warning.main, 0.2),
                bg: isDark ? alpha(theme.palette.warning.main, 0.05) : alpha(theme.palette.warning.light, 0.2),
                text: theme.palette.warning.dark,
                labelColor: 'warning.dark'
            };
        }

        return {
            border: alpha(theme.palette.error.main, 0.2),
            bg: isDark ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.error.light, 0.2),
            text: theme.palette.error.dark,
            labelColor: 'error.dark'
        };
    }, [summary.successRate, theme.palette]);

    return (
        <MainCard title={title} content={false}>
            {/* Toolbar for Search & Filtering */}
            <Box sx={{ p: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search Store ID..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSearch size="1.1rem" stroke={1.5} style={{ color: theme.palette.text.secondary }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.4) : 'common.white',
                                    borderRadius: '12px'
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="status-filter-label">Health Status</InputLabel>
                            <Select
                                labelId="status-filter-label"
                                id="status-filter"
                                value={statusFilter}
                                label="Health Status"
                                onChange={(e) => {
                                    setStatusFilter(e.target.value as any);
                                    setPage(0);
                                }}
                                sx={{
                                    borderRadius: '12px',
                                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.4) : 'common.white'
                                }}
                            >
                                <MenuItem value="all">All Statuses</MenuItem>
                                <MenuItem value="healthy">Healthy (No Errors)</MenuItem>
                                <MenuItem value="error">With Errors</MenuItem>
                                <MenuItem value="inactive">Inactive (No Activity)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="integration-filter-label">Active Integrations</InputLabel>
                            <Select
                                labelId="integration-filter-label"
                                id="integration-filter"
                                value={integrationFilter}
                                label="Active Integrations"
                                onChange={(e) => {
                                    setIntegrationFilter(e.target.value as any);
                                    setPage(0);
                                }}
                                sx={{
                                    borderRadius: '12px',
                                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.4) : 'common.white'
                                }}
                            >
                                <MenuItem value="all">All Integrations</MenuItem>
                                <MenuItem value="both">Prices & Inventory</MenuItem>
                                <MenuItem value="prices">Prices Only</MenuItem>
                                <MenuItem value="inventory">Inventory Only</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Clear Filters" placement="top" arrow>
                            <span>
                                <IconButton
                                    color="secondary"
                                    onClick={handleClearFilters}
                                    disabled={!showClearButton}
                                    sx={{
                                        border: `1px solid ${theme.palette.secondary.main}`,
                                        borderRadius: '12px',
                                        p: '6px',
                                        '&.Mui-disabled': {
                                            border: `1px solid ${theme.palette.action.disabledBackground}`
                                        }
                                    }}
                                >
                                    <FilterListOffIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Box>

            <Divider />

            {/* Store Statistics Summary Cards */}
            {data && (
                <Box sx={{ px: 3, pb: 3, pt: 1 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: '16px',
                                    border: '1px solid',
                                    borderColor:
                                        theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.1) : theme.palette.grey[200],
                                    bgcolor:
                                        theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.2) : theme.palette.grey[50],
                                    textAlign: 'center'
                                }}
                            >
                                <Typography variant="h3" sx={{ fontWeight: 800 }}>
                                    {summary.total}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                                    Total Stores
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: '16px',
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.success.main, 0.2),
                                    bgcolor:
                                        theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.success.main, 0.05)
                                            : alpha(theme.palette.success.light, 0.2),
                                    textAlign: 'center'
                                }}
                            >
                                <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette.success.dark }}>
                                    {summary.healthy}
                                </Typography>
                                <Typography variant="caption" color="success.dark" sx={{ fontWeight: 700 }}>
                                    Healthy Stores
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: '16px',
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.error.main, 0.2),
                                    bgcolor:
                                        theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.error.main, 0.05)
                                            : alpha(theme.palette.error.light, 0.2),
                                    textAlign: 'center'
                                }}
                            >
                                <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette.error.dark }}>
                                    {summary.errors}
                                </Typography>
                                <Typography variant="caption" color="error.dark" sx={{ fontWeight: 700 }}>
                                    Stores with Errors
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: '16px',
                                    border: '1px solid',
                                    borderColor:
                                        theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.1) : theme.palette.grey[300],
                                    bgcolor:
                                        theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.2) : theme.palette.grey[100],
                                    textAlign: 'center'
                                }}
                            >
                                <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                                    {summary.inactive}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                                    Inactive Stores
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: '16px',
                                    border: '1px solid',
                                    borderColor: rateColorSettings.border,
                                    bgcolor: rateColorSettings.bg,
                                    textAlign: 'center'
                                }}
                            >
                                <Typography variant="h3" sx={{ fontWeight: 800, color: rateColorSettings.text }}>
                                    {summary.successRate !== null ? `${summary.successRate.toFixed(1)}%` : 'N/A'}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: rateColorSettings.labelColor }}>
                                    Overall Success Rate
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            )}

            <Divider />

            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((column, colIndex) => (
                                <TableCell
                                    key={column.id}
                                    sortDirection={orderBy === column.id ? order : false}
                                    sx={{
                                        py: 2,
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                        ...(colIndex === 0 ? { pl: 3 } : {})
                                    }}
                                >
                                    {column.sortable ? (
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : 'asc'}
                                            onClick={() => handleRequestSort(column.id as keyof StoreRow)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : (
                                        column.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>{renderTableBodyContent()}</TableBody>
                </Table>
            </TableContainer>

            {data && filteredRows.length > 0 && (
                <>
                    <Divider />
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredRows.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontWeight: 500
                            }
                        }}
                    />
                </>
            )}
            <Divider />
        </MainCard>
    );
};

export default LatestCustomerTableCard;
